import * as _ from 'underscore';
import { getState, getStore } from '../../../store';
import ACTIONS from '../actions';
import { saveWorkoutSetExercises, saveNewWorkoutSet } from '../designer';
import * as ProgramService from '../../../../api/service/programService';
import * as Utils from '../../../../lib/designerUtils';

export const link = async (session, block, nextBlock) => {
    if (linkingBlockInProgress) {
        return;
    }
    // In some cases, the block "key" is set to the react managed key.
    // Block ID in this linking case is more dependable than key.
    startLinkingBlock(block.id);

    setTimeout(async () => {
        await linkBlocksAsync(session, block, nextBlock);
    }, 0);
};

export const linkBlocksAsync = async (session, block, nextBlock) => {
    const dispatch = getStore().dispatch;
    const { exerciseTree, sessionTree } = getState().calendar;
    const setExerciseKeys = [...block.exerciseKeys, ...nextBlock.exerciseKeys];
    const setExercises = [];

    for (let i = 0; i < setExerciseKeys.length; i++) {
        const setExercise = { ...exerciseTree[setExerciseKeys[i]] };
        const isNextBlockExercise = setExercise.set_id !== block.id;

        setExercise.set_id = block.id;
        setExercise.setKey = block.key;
        setExercise.order = i + 1;
        setExercises.push(setExercise);

        dispatch({
            type: ACTIONS.BLOCK_UPDATE_EXERCISE,
            setExercise: setExercise
        });
        if (isNextBlockExercise) {
            dispatch({
                type: ACTIONS.BLOCK_ADD_EXERCISE,
                block,
                exercise: setExercises[i],
                linked: true
            });
        }
    }

    // We have to pull the session from the session tree because
    // in instances where sessions have been pasted in, their downstream
    // usage would have to be different, as they have a different data
    // structure. "Copied" sessions which are not loaded in from the API
    // are not normalized like sessions that are.

    dispatch({
        type: ACTIONS.BLOCK_DELETE,
        session: session.data ? session : sessionTree[session.workout_id],
        block: nextBlock
    });

    await saveWorkoutSetExercises(setExercises, () => {
        removeWorkoutSet(nextBlock);
    });

    dispatch({
        type: ACTIONS.UI_BLOCK_SAVED,
        blockSaved: block.id,
        save: false
    });
    stopLinkingBlock();
};

export const unlink = async (session, block, exercise) => {
    if (linkingBlockInProgress) {
        return;
    }
    startLinkingBlock(block.key);
    setTimeout(async () => {
        await unlinkBlockAsync(session, block, exercise);
    }, 0);
};

export const unlinkBlockAsync = async (session, block, exercise) => {
    const dispatch = getStore().dispatch;

    const newOrder = session.setKeys.indexOf(block.key) + 1;

    const key = Utils.getKey();
    const newBlock = {
        ...block,
        type: Utils.BlockTypes.STRENGTH_POWER,
        exercises: [],
        exerciseKeys: [],
        key,
        id: null,
        instruction: '',
        is_redzone: 0,
        title: Utils.getBlockTypeText({
            type: Utils.BlockTypes.STRENGTH_POWER
        })
    };

    const updatedSession = {
        ...session,
        setKeys: []
    };

    for (let i = 0; i < session.setKeys.length; i++) {
        if (newOrder === i && newOrder !== session.setKeys.length) {
            updatedSession.setKeys.push(newBlock.key);
        }
        updatedSession.setKeys.push(session.setKeys[i]);
    }
    if (newOrder === session.setKeys.length) {
        updatedSession.setKeys.push(newBlock.key);
    }

    dispatch({
        type: ACTIONS.BLOCK_ADD,
        session: updatedSession,
        block: newBlock,
        order: newOrder
    });

    const exerciseIndex = block.exerciseKeys.indexOf(exercise.key) + 1;
    const exerciseTree = getState().calendar.exerciseTree;
    const setExercises = [];

    const firstBlockExercises = [];

    for (let i = 0; i < exerciseIndex; i++) {
        let newExerciseOrder = i + 1;

        const setExercise = {
            ...exerciseTree[block.exerciseKeys[i]],
            order: newExerciseOrder
        };

        dispatch({
            type: ACTIONS.BLOCK_UPDATE_EXERCISE,
            setExercise
        });
        firstBlockExercises.push(setExercise);
    }

    if (firstBlockExercises.length <= 1 && block.instruction) {
        const clearedBlock = {
            ...block,
            instruction: ''
        };
        dispatch({
            type: ACTIONS.BLOCK_UPDATE,
            block: clearedBlock
        });
        await ProgramService.saveWorkoutSet(clearedBlock);
    }

    await saveWorkoutSetExercises(firstBlockExercises, () => {});

    let newExerciseOrder = 1;
    for (let j = exerciseIndex; j < block.exerciseKeys.length; j++) {
        const setExercise = {
            ...exerciseTree[block.exerciseKeys[j]],
            order: newExerciseOrder,
            set_id: null,
            setKey: newBlock.key
        };
        setExercises.push(setExercise);
        dispatch({
            type: ACTIONS.SET_EXERCISE_DELETE,
            block,
            setExercise: exerciseTree[block.exerciseKeys[j]]
        });
        dispatch({
            type: ACTIONS.BLOCK_ADD_EXERCISE,
            block: newBlock,
            exercise: setExercise,
            linked: true
        });
        newExerciseOrder++;
    }

    newBlock.exercises = setExercises;

    const response = await saveNewWorkoutSet(dispatch, newBlock);
    const workoutSetId = response.id;
    for (let k = 0; k < setExercises.length; k++) {
        setExercises[k].set_id = workoutSetId;
        dispatch({
            type: ACTIONS.BLOCK_UPDATE,
            block: setExercises[k]
        });
    }

    await saveWorkoutSetExercises(setExercises, () => {});

    // Update the orders for each of the other workout sets.
    const { blockTree, sessionTree } = getState().calendar;
    const newSession = sessionTree[session.workout_id].data;

    // Reorder the blocks after the index of the newly
    // inserted block. newOrder was ininitialzed to the index
    // of the changed block BEFORE the update occurred, plus 1.
    // Now that we have inserted another block, to move beyond
    // it in the array we need to go one passed the order which
    // is effectivly 2 more than the original index.
    const reorderStart = newOrder + 1;
    for (let j = reorderStart; j < newSession.setKeys.length; j++) {
        const block = blockTree[newSession.setKeys[j]];
        // Orders are 1s based.
        const blockOrder = j + 1;
        dispatch({
            type: ACTIONS.BLOCK_UPDATE,
            block: {
                ...block,
                order: blockOrder
            }
        });
    }

    // Pull the setIds out of the sets themselves because there are
    // inconsistent ids used in setKeys. This gives the API endpoint
    // what it needs to set the proper order.
    const workoutSetIds = newSession.setKeys.map(key => blockTree[key].id);
    await ProgramService.saveWorkoutSetOrder(workoutSetIds);

    // Fin.
    stopLinkingBlock();
};

//private methods
let linkingBlockInProgress = false;
export const startLinkingBlock = blockKey => {
    linkingBlockInProgress = true;
    getStore().dispatch({
        type: ACTIONS.UI_BLOCK_LINKING,
        blockKey
    });
};

export const stopLinkingBlock = _.debounce(() => {
    linkingBlockInProgress = false;
    getStore().dispatch({
        type: ACTIONS.UI_BLOCK_LINKING,
        blockKey: false
    });
}, 20);

async function removeWorkoutSet(workoutSet) {
    await ProgramService.removeProgramWorkoutSet(workoutSet.id);
}
